import { useEffect } from 'react';
import type { PriceQuote } from '@ducat-unit/core';
import { type UseQueryResult, useQuery } from '@tanstack/react-query';
import type { NostrClient } from '@vbyte/nostr-sdk';

import { DEFAULT_PRICE_QUOTE } from '@/constants';
import { useInitialPriceState } from '@/context/InitialPriceHydration';
import { useAppStore, useConfig } from '@/stores/useAppStore';

import { MAX_QUOTE_AGE_SECONDS, parseUsableOracleQuote } from './oracle-price-display';
import { fetchRelayQueryWithFallback } from './oracle-relay-fallback';
import { QueryKeys } from './query-keys';

type BitcoinPriceMempool = {
  time: number;
  USD: number;
};

export function useBitcoinPriceMempool(): UseQueryResult<BitcoinPriceMempool> {
  const path = 'https://mempool.space/api/v1/prices';

  return useQuery({
    queryKey: [QueryKeys.bitcoinPrice],
    queryFn: () => fetch(path).then((res) => res.json()),
    // Refetch every 10 minutes.
    refetchInterval: 10 * 60 * 1000,
    staleTime: 60 * 1000,
  });
}

// These budgets apply only to read-only display quotes, never transaction signing.
export const PRICE_HTTP_FALLBACK_DELAY_MS = 3_000;
export const PRICE_READ_TIMEOUT_MS = 20_000;

function relayWsToHttpApi(relayUrl: string) {
  const url = new URL(relayUrl);
  url.protocol = url.protocol === 'wss:' ? 'https:' : 'http:';
  url.pathname = '/api/query';
  url.search = '';
  url.hash = '';

  return url.toString();
}

async function fetchLatestQuoteFromRelayApi({
  oraclePubkey,
  oracleRelayWS,
  network,
  signal,
}: {
  oraclePubkey: string;
  oracleRelayWS: string;
  network: string;
  signal?: AbortSignal;
}) {
  const payload = await fetchRelayQueryWithFallback(relayWsToHttpApi(oracleRelayWS), {
    method: 'POST',
    signal,
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({
      authors: [oraclePubkey],
      kinds: [10000],
      limit: 5,
    }),
  });

  const { selectVerifiedOracleQuote } = await import('./oracle-price-quote');
  return selectVerifiedOracleQuote(payload, { oraclePubkey, network });
}

async function fetchBitcoinPriceFromRelay({
  oraclePubkey,
  oracleRelayWS,
  network,
  signal,
}: {
  oraclePubkey: string;
  oracleRelayWS: string;
  network: string;
  signal?: AbortSignal;
}) {
  if (signal?.aborted) throw signal.reason;
  // Each attempt owns its socket so cancellation cannot close another query's client.
  let client: NostrClient | undefined;
  const httpController = new AbortController();
  let fallbackTimer: ReturnType<typeof setTimeout> | undefined;
  let deadlineTimer: ReturnType<typeof setTimeout> | undefined;
  let abort: () => void = () => {};
  try {
    return await new Promise<PriceQuote>((resolve, reject) => {
      let settled = false;
      let socketDone = false;
      let httpDone = false;
      let httpStarted = false;
      const finish = (quote?: PriceQuote, error?: unknown) => {
        if (settled) return;
        if (quote) {
          settled = true;
          resolve(quote);
        } else if (error || (socketDone && httpDone)) {
          settled = true;
          reject(error ?? new Error('No BTC price quote returned by oracle relay.'));
        }
      };
      const startHttp = () => {
        if (settled || httpStarted) return;
        httpStarted = true;
        fetchLatestQuoteFromRelayApi({ oraclePubkey, oracleRelayWS, network, signal: httpController.signal }).then(
          (quote) => {
            httpDone = true;
            finish(quote);
          },
          () => {
            httpDone = true;
            finish();
          },
        );
      };
      abort = () => finish(undefined, signal?.reason ?? new DOMException('Price request was superseded', 'AbortError'));
      signal?.addEventListener('abort', abort, { once: true });
      fallbackTimer = setTimeout(startHttp, PRICE_HTTP_FALLBACK_DELAY_MS);
      deadlineTimer = setTimeout(
        () => finish(undefined, new Error('No BTC price quote returned before the read deadline.')),
        PRICE_READ_TIMEOUT_MS,
      );
      Promise.resolve()
        .then(async () => {
          if (settled) return;
          const [{ createPriceQuoteTransport }, { fetchVerifiedSocketQuote }] = await Promise.all([
            import('./price-quote-transport'),
            import('./oracle-price-quote'),
          ]);
          if (settled) return;
          client = createPriceQuoteTransport(oracleRelayWS, PRICE_READ_TIMEOUT_MS);
          return fetchVerifiedSocketQuote({ nostr: client }, { oraclePubkey, network });
        })
        .then(
          (quote) => {
            socketDone = true;
            if (!quote) startHttp();
            finish(quote);
          },
          () => {
            socketDone = true;
            startHttp();
            finish();
          },
        );
    });
  } finally {
    clearTimeout(fallbackTimer);
    clearTimeout(deadlineTimer);
    signal?.removeEventListener('abort', abort);
    httpController.abort();
    try {
      client?.close();
    } catch {
      /* SDK browser cleanup is best effort. */
    }
  }
}

// How long a fetched price quote is considered fresh. Kept short (below the
// oracle's price-contract validity window) so that a quote left sitting in the
// store goes stale and gets refetched — otherwise a long-open/backgrounded tab
// keeps deriving price-commit hashes from an expired quote and the oracle no
// longer has a matching contract, so the action silently fails.
export const PRICE_QUOTE_STALE_MS = 60 * 1000; // 60s

export type PriceFeedStatus = 'loading' | 'available' | 'unavailable';

export function useBitcoinPrice(): Omit<UseQueryResult<PriceQuote>, 'data'> & {
  data: BitcoinPriceMempool | undefined;
  priceStatus: PriceFeedStatus;
} {
  const config = useConfig();
  const setPriceQuote = useAppStore((s) => s.setPriceQuote);
  const storedQuote = useAppStore((s) => s.priceQuote);
  const context = { oraclePubkey: config?.oraclePubkey || '', network: config?.network || '' };
  const storedUsableQuote = parseUsableOracleQuote(storedQuote, context);
  const queryKey = [QueryKeys.bitcoinPrice, config?.network, config?.oraclePubkey, config?.oracleRelayWS];
  const initialPrice = useInitialPriceState(queryKey);

  useEffect(() => {
    // The timer clears the same quote in the shared store, so every mounted
    // display/action consumer observes expiry together. Never clear a newer
    // quote.
    if (
      !config ||
      !parseUsableOracleQuote(storedQuote, { oraclePubkey: config.oraclePubkey, network: config.network }, storedQuote.base_stamp)
    )
      return;
    const timer = setTimeout(
      () => {
        const state = useAppStore.getState();
        if (state.priceQuote === storedQuote) {
          setPriceQuote({ ...DEFAULT_PRICE_QUOTE, chain_network: config.network });
        }
      },
      Math.max(0, (storedQuote.base_stamp + MAX_QUOTE_AGE_SECONDS + 1) * 1000 - Date.now()),
    );
    return () => clearTimeout(timer);
  }, [storedQuote, setPriceQuote, config]);

  const query = useQuery({
    enabled: !initialPrice.pending && !initialPrice.hydrating && !!config?.network && !!config?.oraclePubkey && !!config.oracleRelayWS,
    queryKey,
    queryFn: async ({ signal }) => {
      if (!config) throw new Error('Network config is not ready.');
      const quote = await fetchBitcoinPriceFromRelay({ ...config, signal });
      // Do not publish a response after React Query cancels its request.
      if (signal.aborted) throw new DOMException('Price request was superseded', 'AbortError');
      return quote;
    },
    // Refetch every 2 minutes, and keep polling even when the tab is hidden so a
    // backgrounded page does not hold a stale quote.
    refetchInterval: 2 * 60 * 1000,
    refetchIntervalInBackground: true,
    // Finite staleTime (was Infinity) so react-query treats an old quote as stale
    // and refetches it on window focus / reconnect — the recovery path for a page
    // that has been open a long time. staleTime: Infinity disabled that entirely.
    staleTime: PRICE_QUOTE_STALE_MS,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    retry: true,
    retryDelay: 15 * 1000, // 15s
  });
  useEffect(() => {
    if (!config || !query.data) return;
    const cached = parseUsableOracleQuote(query.data, { oraclePubkey: config.oraclePubkey, network: config.network });
    if (!cached) return;
    const state = useAppStore.getState();
    const current = parseUsableOracleQuote(state.priceQuote, { oraclePubkey: config.oraclePubkey, network: config.network });
    if (!current || current.base_stamp < cached.base_stamp) setPriceQuote(cached);
  }, [query.data, config, setPriceQuote]);
  useEffect(() => {
    if (!config || !query.data || initialPrice.hydrating || initialPrice.pending) return;
    let cancelled = false;
    void import('./oracle-price-quote').then(({ getRetainedOracleQuote }) => {
      if (cancelled || getRetainedOracleQuote(query.data)) return;
      void query.refetch().then(({ data }) => {
        if (cancelled || !data || !getRetainedOracleQuote(data)) return;
        const state = useAppStore.getState();
        const current = parseUsableOracleQuote(state.priceQuote, { oraclePubkey: config.oraclePubkey, network: config.network });
        // React Query may retain the same numeric object. Publish a fresh store
        // object so a repayment preview retries once its signature is available.
        if (current && current.base_stamp === data.base_stamp) setPriceQuote({ ...data });
      });
    });
    return () => {
      cancelled = true;
    };
  }, [query.data, query.refetch, initialPrice.hydrating, initialPrice.pending, config, setPriceQuote]);
  const cachedUsableQuote = parseUsableOracleQuote(query.data, context);
  const usableQuote = initialPrice.hydrating
    ? undefined
    : !storedUsableQuote || (cachedUsableQuote && cachedUsableQuote.base_stamp > storedUsableQuote.base_stamp)
      ? cachedUsableQuote
      : storedUsableQuote;
  return {
    ...query,
    priceStatus: initialPrice.hydrating
      ? 'loading'
      : usableQuote
        ? 'available'
        : query.failureCount > 0 || query.isError || query.fetchStatus === 'paused' || query.data
          ? 'unavailable'
          : 'loading',
    data: usableQuote ? { USD: usableQuote.base_price, time: usableQuote.base_stamp } : undefined,
  };
}
